import { Spiner } from "@components/loaders/spiner";
import { SchoolService } from "@services/school.service";
import { usePageHeader } from "@hooks/use_page_header";
import { useMutation } from "@tanstack/react-query";
import { Button, Form, Input, message } from "antd";
import { NoticeType } from "antd/es/message/interface";
import { useEffect, useState } from "react";

import { ProfileAvatarHeader } from "@components/general/profile_avatar_header";
import { useStudentBiography } from "@hooks/use_student_biography";

export const BiographPage = () => {
  usePageHeader("Biodata", { bottomNavigation: "hidden" });
  const [messageApi, contextHolder] = message.useMessage();
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [showMessageText, setShowMessageText] = useState<string>("");
  const [messageType, setMessageType] = useState<string>("");
  const [form] = Form.useForm();

  const { biography, isPending, gender, refetch } = useStudentBiography();

  useEffect(() => {
    if (biography) {
      form.setFieldsValue({
        nis: biography.nis,
        namasiswa: biography.namasiswa,
        kotaLahir: biography.kotalahir,
        tanggalLahir: biography.tanggallahir,
        namaAyah: biography.namaayah,
        namaIbu: biography.namaibu,
        pekerjaanAyah: biography.pekerjaanayah,
        pekerjaanIbu: biography.pekerjaanibu,
        alamat: biography.alamat,
        kelas: biography.kelas,
        nama: biography.nama,
      });
    }
  }, [biography, form]);

  const { mutate: onUpdateBio, isPending: isPendingUpdateBio } = useMutation({
    mutationFn: SchoolService.updateStudentBiography,
    onSuccess: (response) => {
      setShowMessage(true);
      setMessageType("success");
      setShowMessageText(response.message);

      refetch();
    },
    onError: (error: any) => {
      setShowMessage(true);
      setMessageType("error");
      setShowMessageText(error.message ?? "Internal Error");
    },
  });

  useEffect(() => {
    if (showMessage) {
      messageApi.open({
        type: messageType as NoticeType,
        content: <>{showMessageText}</>,
      });
    }
    setShowMessage(false);
  }, [showMessage, showMessageText, messageType, messageApi]);

  return (
    <>
      {contextHolder}
      <div className="tw-px-4 tw-pt-2 tw-pb-20">
        <Form
          form={form}
          className=""
          autoComplete="off"
          onFinish={({ kelas, nama, ...payload }) => onUpdateBio(payload)}
        >
          <div className="tw-mb-4 tw-mt-12 tw-pt-14 tw-relative tw-text-center card-body">
            <ProfileAvatarHeader
              foto={biography?.foto}
              namasiswa={biography?.namasiswa}
              kelas={biography?.kelas}
              marhalah={biography?.marhalah}
              gender={gender}
              isPending={isPending}
            />

            <div className="tw-my-4 divider"></div>
            {isPending ? (
              <div className="tw-p-8 tw-text-center">
                <Spiner />
              </div>
            ) : (
              <div className="student-bio">
                <Form.Item label="NIS" name="nis" className="tw-mb-4">
                  <Input
                    className="tw-shadow-none tw-rounded-xl tw-bg-background-200 tw-border-background-300"
                    placeholder="NIS"
                    disabled
                  />
                </Form.Item>

                <Form.Item label="Nama" name="namasiswa" className="tw-mb-4">
                  <Input
                    className="tw-shadow-none tw-rounded-xl tw-bg-background-200 tw-border-background-300"
                    placeholder="Nama"
                  />
                </Form.Item>

                <Form.Item
                  label="Tempat Lahir"
                  name="kotaLahir"
                  className="tw-mb-4"
                >
                  <Input
                    className="tw-shadow-none tw-rounded-xl tw-bg-background-200 tw-border-background-300"
                    placeholder="Kota Lahir"
                  />
                </Form.Item>

                <Form.Item
                  label="Tanggal Lahir"
                  name="tanggalLahir"
                  className="tw-mb-4"
                >
                  <Input
                    className="tw-shadow-none tw-rounded-xl tw-bg-background-200 tw-border-background-300"
                    type="date"
                  />
                </Form.Item>
              </div>
            )}
          </div>

          {isPending ? (
            <div className="tw-p-8 tw-text-center card-body">
              <Spiner />
            </div>
          ) : (
            <>
              <div className="tw-mb-4 card-body">
                <Form.Item
                  label="Nama Ayah"
                  name="namaAyah"
                  className="tw-mb-4"
                >
                  <Input
                    className="tw-shadow-none tw-rounded-xl tw-bg-background-200 tw-border-background-300"
                    placeholder="Nama Ayah"
                  />
                </Form.Item>

                <Form.Item label="Nama Ibu" name="namaIbu" className="tw-mb-4">
                  <Input
                    className="tw-shadow-none tw-rounded-xl tw-bg-background-200 tw-border-background-300"
                    placeholder="Nama Ibu"
                  />
                </Form.Item>

                <Form.Item
                  label="Pekerjaan Ayah"
                  name="pekerjaanAyah"
                  className="tw-mb-4"
                >
                  <Input
                    className="tw-shadow-none tw-rounded-xl tw-bg-background-200 tw-border-background-300"
                    placeholder="Pekerjaan Ayah"
                  />
                </Form.Item>

                <Form.Item
                  label="Pekerjaan Ibu"
                  name="pekerjaanIbu"
                  className="tw-mb-4"
                >
                  <Input
                    className="tw-shadow-none tw-rounded-xl tw-bg-background-200 tw-border-background-300"
                    placeholder="Pekerjaan Ibu"
                  />
                </Form.Item>

                <Form.Item label="Alamat" name="alamat" className="tw-mb-4">
                  <Input
                    className="tw-shadow-none tw-rounded-xl tw-bg-background-200 tw-border-background-300"
                    placeholder="Alamat"
                  />
                </Form.Item>
              </div>

              <div className="tw-mb-4 card-body">
                <Form.Item label="Kelas" name="kelas" className="tw-mb-4">
                  <Input
                    className="tw-shadow-none tw-rounded-xl tw-bg-background-200 tw-border-background-300"
                    placeholder="Kelas"
                    disabled
                  />
                </Form.Item>

                <Form.Item label="Wali Kelas" name="nama" className="tw-mb-4">
                  <Input
                    className="tw-shadow-none tw-rounded-xl tw-bg-background-200 tw-border-background-300"
                    placeholder="Wali Kelas"
                    disabled
                  />
                </Form.Item>
              </div>
              <Button
                type="primary"
                htmlType="submit"
                loading={isPendingUpdateBio}
                className="tw-p-2 tw-mt-4 tw-h-10 tw-w-full tw-text-base tw-rounded-xl bg-gradient tw-text-neutral-background focus:tw-shadow-none hover:tw-bg-background-200 hover:tw-text-primary hover:tw-border-primary"
              >
                {isPendingUpdateBio ? "" : "Update"}
              </Button>
            </>
          )}
        </Form>
      </div>
    </>
  );
};
